import * as XLSX from "xlsx-js-style";
import { csvCell, triggerDownload } from "@/features/exports/services/_helpers";
import { downloadWorkbook } from "@/features/exports/services/_xlsx";
import {
  getDeficiencyCategory,
  getDeficiencyTitle,
  type DeficiencySeverity,
  type RawDeficiency,
} from "./deficiencyHelpers";

const DEFICIENCY_HEADERS = ["Title", "Group", "Severity", "Detail", "Recommendation", "Asset", "Asset Category", "Survey Field"];
const STEPS_HEADERS = ["Category", "Priority", "Description", "Cost"];

export type RecommendedStepSection = {
  category: string;
  priority: DeficiencySeverity;
  totalCost: number;
  lineItems: { description: string; cost: number | null }[];
};

function getDeficiencyExportRows(defList: RawDeficiency[]) {
  return defList.map((d) => [
    getDeficiencyTitle(d),
    getDeficiencyCategory(d),
    d.severity ?? "",
    d.detail ?? "",
    d.recommendation_summary ?? "",
    d.asset?.name ?? "",
    d.asset?.category ?? "",
    d.survey_field ?? "",
  ]);
}

export function exportDeficienciesCSV(defList: RawDeficiency[]) {
  const rows = getDeficiencyExportRows(defList);
  const csv = [DEFICIENCY_HEADERS.join(","), ...rows.map((r) => r.map(csvCell).join(","))].join("\n");
  triggerDownload(csv, "deficiencies.csv", "text/csv;charset=utf-8;");
}

export function exportDeficienciesXLSX(defList: RawDeficiency[]) {
  const rows = getDeficiencyExportRows(defList);
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet([DEFICIENCY_HEADERS, ...rows]);
  worksheet["!cols"] = [
    { wch: 36 },
    { wch: 24 },
    { wch: 14 },
    { wch: 48 },
    { wch: 48 },
    { wch: 28 },
    { wch: 24 },
    { wch: 32 },
  ];
  XLSX.utils.book_append_sheet(workbook, worksheet, "Deficiencies");
  downloadWorkbook(workbook, "deficiencies.xlsx");
}

function getRecommendedStepExportRows(sections: RecommendedStepSection[]) {
  const rows: Array<[string, string, string, number | ""]> = [];
  sections.forEach((section) => {
    section.lineItems.forEach((item) => {
      rows.push([section.category, section.priority, item.description, item.cost != null ? item.cost : ""]);
    });
  });
  return rows;
}

export function exportStepsCSV(sections: RecommendedStepSection[]) {
  const rows = getRecommendedStepExportRows(sections);
  const csvRows = rows.map((row) => row.map(csvCell).join(","));
  const csv = [STEPS_HEADERS.join(","), ...csvRows].join("\n");
  triggerDownload(csv, "recommended_steps.csv", "text/csv;charset=utf-8;");
}

export function exportStepsXLSX(sections: RecommendedStepSection[]) {
  const rows = getRecommendedStepExportRows(sections);
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet([STEPS_HEADERS, ...rows]);
  worksheet["!cols"] = [{ wch: 24 }, { wch: 14 }, { wch: 60 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(workbook, worksheet, "Recommended Steps");
  downloadWorkbook(workbook, "recommended_steps.xlsx");
}
